// export enums for clarity
export enum ReportPeriod {
  Daily = 'DAILY',
  Weekly = 'WEEKLY',
  Monthly = 'MONTHLY',
  Yearly = 'YEARLY',
  Custom = 'CUSTOM',
}

export enum MenuItemCategory {
  TopSellers = 'TOP_SELLERS',
  DOGS = 'DOGS',
}

export enum BreakTypes {
  Missed = 'MISSED',
  Exceeded = 'EXCEEDED',
  Waived = 'WAIVED',
}

// export interfaces defining comparison data for sales, labor costs, etc.
export interface ComparisonData {
  currentAmount: number;
  SDLM?: number; // Same Day Last Month
  SDLY?: number; // Same Day Last Year
  SWLM?: number; // Same Week Last Month
  SWLY?: number; // Same Week Last Year
  SMLY?: number; // Same Month Last Year
  LastYear?: number;
  TwoYearsAgo?: number; // Two years ago
}

export interface NetSales extends ComparisonData {
  net?: number | null;
}
export interface LaborCost extends ComparisonData {
  percentageOfGrossSales: number;
}

interface Voids {
  percentageOfNetSales: number;
  amout: number;
}
interface Comps {
  percentageOfNetSales: number;
  amout: number;
}
export interface VoidsAndComps {
  voids: Voids;
  comps: Comps;
}

interface TopSellers {
  quantity: number;
  totalSales: number;
}
interface Dogs {
  quantity: number;
  totalSales: number;
}
export interface Menu {
  topSellers: TopSellers;
  dogs: Dogs;
}

export interface BreakViolation {
  [BreakTypes.Missed]: number;
  [BreakTypes.Exceeded]: number;
}

// Generic overview export interface for all metrics, period included at the overview level
export interface ReportsOverview {
  period: ReportPeriod;
  netSales: NetSales;
  voidsAndComps: VoidsAndComps;
  menu: Menu;
  laborCosts: LaborCost;
  breakViolations: BreakViolation;
}

// net sale details based on period

// Hourly sales interface
export interface HourlyNetSales {
  hour: number;
  sales: number;
  same_hour_last_month: number;
  same_hour_last_year: number;
}

export interface DailyNetSales {
  day: number;
  sales: number;
  same_day_last_month: number;
  percentage_change: number;
  same_day_last_year: number;
  hourlyBreakdown?: HourlyNetSales[];
}

// Weekly sales interface adjusted for daily breakdowns
export interface WeeklyNetSales {
  week: number;
  sales: number;
  same_week_last_month: number;
  percentage_change: number;
  same_week_last_year: number;
  dailyBreakdown?: DailyNetSales[];
}

// Monthly sales interface adjusted for weekly breakdowns
export interface MonthlyNetSales {
  month: number;
  sales: number;
  same_month_last_year: number;
  same_month_before_last_year: number;
  percentage_change: number;
  weeklyBreakdown?: WeeklyNetSales[];
}

// Yearly sales interface adjusted for monthly breakdowns
export interface YearlyNetSales {
  year: number;
  sales: number;
  last_year: number;
  percentage_change: number;
  monthlyBreakdown?: MonthlyNetSales[];
}

export interface NetSaleDetails {
  period: ReportPeriod;
  current_period_amount: number;
  percentage_change: number;
  period_breakdown: HourlyNetSales[] | DailyNetSales[] | WeeklyNetSales[] | MonthlyNetSales[] | YearlyNetSales[];
}

// labour cost types

// Hourly labor cost interface
export interface HourlyLaborCosts {
  hour: number;
  laborCost: number;
  same_hour_last_month: number;
  same_hour_last_year: number;
}

// Daily labor cost interface with optional hourly breakdown
export interface DailyLaborCosts {
  day: number;
  laborCost: number;
  same_day_last_month: number;
  percentage_change: number;
  same_day_last_year: number;
  hourlyBreakdown?: HourlyLaborCosts[];
}

// Weekly labor cost interface with optional daily breakdown
export interface WeeklyLaborCosts {
  week: number;
  laborCost: number;
  same_week_last_month: number;
  percentage_change: number;
  same_week_last_year: number;
  dailyBreakdown?: DailyLaborCosts[];
}

// Monthly labor cost interface with optional weekly breakdown
export interface MonthlyLaborCosts {
  month: number;
  laborCost: number;
  same_month_last_year: number;
  same_month_before_last_year: number;
  percentage_change: number;
  weeklyBreakdown?: WeeklyLaborCosts[];
}

// Yearly labor cost interface with optional monthly breakdown
export interface YearlyLaborCosts {
  year: number;
  laborCost: number;
  last_year: number;
  percentage_change: number;
  monthlyBreakdown?: MonthlyLaborCosts[];
}

export interface LaborCostDetails {
  period: ReportPeriod;
  current_period_amount: number;
  percentage_change: number;
  period_breakdown:
    | HourlyLaborCosts[]
    | DailyLaborCosts[]
    | WeeklyLaborCosts[]
    | MonthlyLaborCosts[]
    | YearlyLaborCosts[];
}

export interface ReportsBreakDetail {
  breakTaken: boolean;
  payable: boolean;
  type: string;
  duration: number;
  totalHours: number;
  roleId?: number;
  roleName?: string;
}

export interface ReportsTimeData {
  clockingInfo: any[];
  breaksList: ReportsBreakDetail[];
  totalWorkingTime: number;
}

export interface ReportsEmployee {
  employeeId: string;
  employeeName: string;
  roleId: number;
  roleName: string;
  timeData: ReportsTimeData;
}

export interface ReportsAggregatedEmployee {
  employeeId: string;
  employeeName: string;
  totalBreaks: number;
  missedBreaks: number;
  takenBreaks: number;
  breaksDetails: ReportsBreakDetail[];
}
